/**
 * chainGifExport.js
 *
 * Records the chain reveal sequence as an animated GIF.
 * Reuses the same rendering pipeline as chainVideoExport.js.
 *
 * Uses gifenc for encoding — lightweight, no web workers needed.
 */

import { GIFEncoder, quantize, applyPalette } from 'gifenc';
import { SIZE, buildSetup, renderFrame } from './chainVideoExport';

// GIF-specific settings — lower FPS for reasonable file size
const GIF_FPS        = 10;
const GIF_SIZE       = 480;  // smaller than video for file size

/**
 * Record the chain reveal as an animated GIF.
 *
 * @param {object}   chain      — chain object with .steps[] and .original_author
 * @param {function} onProgress — called with 0–1 as encoding progresses
 * @returns {Promise<{blob: Blob, mimeType: string}>}
 */
export async function recordChainGif(chain, onProgress) {
  const steps = chain.steps || [];
  if (steps.length === 0) return null;

  const { drawInfos, timeline, totalDuration } = buildSetup(steps);

  // Full-size render canvas (matching video export)
  const renderCanvas = document.createElement('canvas');
  renderCanvas.width  = SIZE;
  renderCanvas.height = SIZE;
  const renderCtx = renderCanvas.getContext('2d');

  // Downscaled canvas for GIF frames
  const gifCanvas = document.createElement('canvas');
  gifCanvas.width  = GIF_SIZE;
  gifCanvas.height = GIF_SIZE;
  const gifCtx = gifCanvas.getContext('2d');
  gifCtx.imageSmoothingEnabled = true;
  gifCtx.imageSmoothingQuality = 'medium';

  const gif = GIFEncoder();
  const frameDurMs  = 1000 / GIF_FPS;
  const totalFrames = Math.ceil(totalDuration / frameDurMs);
  const delay       = Math.round(frameDurMs); // GIF frame delay in ms

  for (let fi = 0; fi < totalFrames; fi++) {
    const elapsed = fi * frameDurMs;

    // Render at full size, then downscale
    renderFrame(renderCtx, steps, timeline, drawInfos, elapsed);
    gifCtx.drawImage(renderCanvas, 0, 0, GIF_SIZE, GIF_SIZE);

    // Extract pixel data and quantize
    const imageData = gifCtx.getImageData(0, 0, GIF_SIZE, GIF_SIZE);
    const palette = quantize(imageData.data, 256);
    const index = applyPalette(imageData.data, palette);

    gif.writeFrame(index, GIF_SIZE, GIF_SIZE, { palette, delay });

    if (onProgress) onProgress(Math.min(elapsed / totalDuration, 0.99));

    // Yield every 10 frames to keep UI responsive
    if (fi % 10 === 0) {
      await new Promise(r => setTimeout(r, 0));
    }
  }

  gif.finish();

  if (onProgress) onProgress(1);

  const blob = new Blob([gif.bytes()], { type: 'image/gif' });
  return { blob, mimeType: 'image/gif' };
}
